import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../../utils/navigation';
import authService from '../../api/auth';
import {
  Box, TextField, Button, Typography, Container, IconButton,
  InputAdornment, Alert, CircularProgress, Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Google as GoogleIcon } from '@mui/icons-material';
import { signInWithPopup } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../../api/config';
import { createUserProfile } from '../../api/users';
import { navigateByRole } from '../../utils/navigation';
import AuthNavbar from '../layout/AuthNavbar';

const LoginForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const { login } = useAuth();
  const { t } = useTranslation();

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      if (!formData.email || !formData.password) throw new Error(t('auth.enterCredentials'));
      const response = await authService.login(formData.email, formData.password);
      await login(response.user, response.token);
      navigateByRole(navigate, response.user.role);
    } catch (err) {
      setError(err.message || t('auth.loginFailed'));
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      let userProfile;
      if (!userDoc.exists()) {
        const userData = {
          id: user.uid, email: user.email,
          displayName: user.displayName || 'User',
          firstName: user.displayName?.split(' ')[0] || 'User',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          createdAt: new Date().toISOString(), isNewUser: true, photoURL: user.photoURL
        };
        await createUserProfile(user.uid, userData);
        userProfile = userData;
      } else {
        userProfile = { ...userDoc.data(), id: user.uid };
      }
      const token = await user.getIdToken();
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({ id: user.uid, ...userProfile }));
      await login(userProfile, token);
      if (!userDoc.exists() || userProfile.isNewUser) {
        navigate('/auth/role-selection');
      } else if (userProfile.role) {
        navigateByRole(navigate, userProfile.role);
      } else {
        setError(t('auth.incompleteProfile'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <>
      <AuthNavbar />
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FAFAFA', pt: 8 }}>
        <Container maxWidth="sm">
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3,
            boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
            p: { xs: 3, sm: 5 }, mx: 'auto', maxWidth: 440
          }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box component="img" src="/images/lingoply-logo.jpg" alt="Lingoply"
                sx={{ width: 64, height: 64, borderRadius: '50%', mb: 1.5 }} />
              <Typography variant="h5" fontWeight={700} color="#1a1a1a">{t('auth.signIn')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('auth.welcomeBack')}</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField fullWidth required label={t('auth.emailAddress')} name="email" autoComplete="email" autoFocus
              value={formData.email} onChange={handleChange} sx={{ mb: 2 }} />

            <TextField fullWidth required name="password" label={t('auth.password')}
              type={showPassword ? 'text' : 'password'} autoComplete="current-password"
              value={formData.password} onChange={handleChange} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )}}
            />

            <Box sx={{ textAlign: 'right', mb: 2.5 }}>
              <Link to={`/${getCurrentLanguage()}/auth/forgot-password`}
                style={{ textDecoration: 'none', color: '#F5A623', fontWeight: 500, fontSize: '0.85rem' }}>
                {t('auth.forgotPassword.link')}
              </Link>
            </Box>

            <Button fullWidth variant="contained" disabled={loading} onClick={handleLogin}
              sx={{ py: 1.5, mb: 2, fontSize: '1rem', fontWeight: 600, borderRadius: 2.5,
                bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}>
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('auth.signIn')}
            </Button>

            <Divider sx={{ my: 2, color: '#bbb', fontSize: '0.8rem' }}>OR</Divider>

            <Button fullWidth variant="outlined" startIcon={<GoogleIcon />} onClick={handleGoogleLogin}
              sx={{ py: 1.3, mb: 3, fontWeight: 500, fontSize: '0.95rem', borderRadius: 2.5,
                color: '#333', borderColor: '#ddd', '&:hover': { borderColor: '#F5A623', bgcolor: '#FFF8F0' } }}>
              {t('auth.continueWithGoogle')}
            </Button>

            <Typography variant="body2" textAlign="center" color="text.secondary">
              {t('auth.noAccount')}{' '}
              <Link to={`/${getCurrentLanguage()}/auth/register`}
                style={{ textDecoration: 'none', color: '#F5A623', fontWeight: 600 }}>
                {t('auth.signUpFree')}
              </Link>
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LoginForm;
